import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Mail } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useCartStore } from '../store/cartStore';

// Common dial codes, most relevant markets first. Not exhaustive, but
// covers the regions this store actually ships to and sells in.
const DIAL_CODES = [
  { code: '+234', country: 'NG', label: '🇳🇬 +234' },
  { code: '+1', country: 'US', label: '🇺🇸 +1' },
  { code: '+44', country: 'GB', label: '🇬🇧 +44' },
  { code: '+233', country: 'GH', label: '🇬🇭 +233' },
  { code: '+254', country: 'KE', label: '🇰🇪 +254' },
  { code: '+27', country: 'ZA', label: '🇿🇦 +27' },
  { code: '+1', country: 'CA', label: '🇨🇦 +1' },
  { code: '+61', country: 'AU', label: '🇦🇺 +61' },
  { code: '+33', country: 'FR', label: '🇫🇷 +33' },
  { code: '+49', country: 'DE', label: '🇩🇪 +49' },
  { code: '+971', country: 'AE', label: '🇦🇪 +971' },
  { code: '+81', country: 'JP', label: '🇯🇵 +81' },
  { code: '+41', country: 'CH', label: '🇨🇭 +41' },
];

// Maps the two-letter country code ipapi.co returns to a dial code above.
const COUNTRY_TO_DIAL: Record<string, string> = {
  NG: '+234', US: '+1', GB: '+44', GH: '+233', KE: '+254',
  ZA: '+27', CA: '+1', AU: '+61', FR: '+33', DE: '+49',
  AE: '+971', JP: '+81', CH: '+41',
};

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
  });
  const [dialCode, setDialCode] = useState('+234');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showVerifyPrompt, setShowVerifyPrompt] = useState(false);
  const { register } = useAuth();
  const { fetchCart } = useCartStore();
  const navigate = useNavigate();

  // Auto-detect the customer's country from their IP to pre-select the
  // right dial code, the same way currency auto-detection already works.
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
        if (res.ok) {
          const data = await res.json();
          const matched = COUNTRY_TO_DIAL[data.country_code];
          if (matched) setDialCode(matched);
        }
      } catch {
        // Keep the default (+234) if detection fails — never block signup.
      }
    };
    detectCountry();
  }, []);

  // Higher-contrast input style for this form specifically — the shared
  // .input-luxury class uses very light borders/placeholders that were
  // reported as hard to read here.
  const inputClass =
    'w-full bg-white border-2 border-secondary/40 focus:border-primary rounded-sm px-4 py-3 text-sm text-primary placeholder:text-secondary/70 placeholder:tracking-widest placeholder:text-xs outline-none transition-colors';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await register({ ...formData, phone: formData.phone ? `${dialCode} ${formData.phone}` : '' });
      await fetchCart();
      setShowVerifyPrompt(true);
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    setShowVerifyPrompt(false);
    navigate('/account');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Brand Panel */}
<div className="hidden lg:block flex-1 relative overflow-hidden">
  <img
    src="https://images.unsplash.com/photo-1509631179647-0177331693ae?w=1200&q=80"
    alt="Lanre Savage"
    className="w-full h-full object-cover"
  />
  <div className="absolute inset-0 bg-black/50" />
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1, delay: 0.3 }}
    className="absolute inset-0 flex items-center justify-center text-center px-12"
  >
    <div>
      <h2 className="font-serif text-5xl text-white mb-6 leading-tight">
        Begin Your<br />Journey
      </h2>
      <p className="text-xs tracking-widest uppercase text-white/70 max-w-xs mx-auto leading-relaxed">
        Join the Lanre Savage community and discover a world of timeless luxury.
      </p>
    </div>
  </motion.div>
</div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-sm"
        >
          <div className="text-center mb-12">
            <Link to="/">
              <img src="/logo.png" alt="Lanre Savage" className="h-16 mx-auto" />
            </Link>
            <p className="text-xs tracking-widest uppercase text-secondary font-medium mt-4">
              Join Us
            </p>
            <h1 className="font-serif text-3xl text-primary mt-2">
              Create Account
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="FIRST NAME"
                required
                className={inputClass}
              />
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="LAST NAME"
                required
                className={inputClass}
              />
            </div>

            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="EMAIL ADDRESS"
              required
              className={inputClass}
            />

            <div className="flex space-x-2">
              <select
                value={dialCode}
                onChange={(e) => setDialCode(e.target.value)}
                className={inputClass}
                style={{ width: '110px', flex: '0 0 110px' }}
              >
                {DIAL_CODES.map((d) => (
                  <option key={`${d.country}-${d.code}`} value={d.code}>
                    {d.label}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="WHATSAPP NUMBER"
                required
                className={inputClass}
                style={{ flex: '1 1 0%', minWidth: 0, width: 'auto' }}
              />
            </div>
            <p className="text-xs text-secondary -mt-3">
              We'll use this to reach you on WhatsApp about your orders.
            </p>

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="PASSWORD"
                required
                minLength={8}
                className={`${inputClass} pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <p className="text-xs text-secondary tracking-wide">
              Minimum 8 characters
            </p>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full disabled:opacity-50"
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="text-center mt-8">
            <p className="text-xs tracking-widest uppercase text-secondary">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-primary hover:text-secondary underline transition-colors"
              >
                Sign In
              </Link>
            </p>
          </div>

          <p className="text-xs text-secondary text-center mt-6 leading-relaxed tracking-wide">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="underline hover:text-primary transition-colors">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline hover:text-primary transition-colors">
              Privacy Policy
            </Link>
          </p>
        </motion.div>
      </div>

      <AnimatePresence>
        {showVerifyPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white max-w-sm w-full p-8 text-center"
            >
              <div className="w-14 h-14 bg-light rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail size={22} className="text-primary" />
              </div>
              <h2 className="font-serif text-2xl text-primary mb-3">Check Your Email</h2>
              <p className="text-sm text-secondary leading-relaxed mb-8">
                We've sent a verification link to <span className="text-primary font-medium">{formData.email}</span>. Please verify your account before placing an order.
              </p>
              <button onClick={handleContinue} className="btn-primary w-full">
                Continue
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RegisterPage;